import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { syncListingToBrowseCache } from '../../lib/browseListingsCache';
import { PUBLISHED_LISTINGS_QUERY_KEY } from '../../hooks/usePublishedListings';
import toast from 'react-hot-toast';
import type { Listing } from '@shared/types';
import GoogleMapsAutocomplete from '../../components/GoogleMapsAutocomplete';
import PriceRulesEditor from '../../components/PriceRulesEditor';

function slugifyForUrl(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const PRESET_AMENITIES = [
  'WiFi',
  'Pool',
  'Sea view',
  'Parking',
  'Braai / BBQ',
  'Air conditioning',
  'DSTV / Netflix',
  'Washing machine',
  'Dishwasher',
  'Pet friendly',
  'Wheelchair accessible',
  'Security',
  'Daily housekeeping',
];

export default function ListingEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<Listing>>({
    title: '',
    description: '',
    basePrice: 0,
    bedrooms: 1,
    bathrooms: 1,
    capacity: 2,
    minNights: 1,
    maxNights: 30,
    amenities: [],
    tags: [],
    priceRules: [],
    status: 'published',
    featured: false,
    slug: '',
    location: {
      lat: 0,
      lng: 0,
      address: '',
      city: '',
      country: 'South Africa',
    },
  });

  useEffect(() => {
    if (id) {
      const fetchListing = async () => {
        const docRef = doc(db, 'listings', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setFormData({ id: docSnap.id, ...docSnap.data() } as Listing);
        }
      };
      fetchListing();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      const slugVal = slugifyForUrl((formData.slug as string) || '');
      const listingData = {
        ...formData,
        hostId: currentUser.uid,
        updatedAt: Timestamp.now(),
        ...(id ? {} : { createdAt: Timestamp.now() }),
        // Ensure status is set
        status: formData.status || 'draft',
        // Ensure required fields
        images: formData.images || [],
        amenities: formData.amenities || [],
        tags: formData.tags || [],
        priceRules: formData.priceRules || [],
        featured: !!formData.featured,
        coverImageUrl: formData.coverImageUrl || undefined,
        ...(slugVal ? { slug: slugVal } : { slug: '' }),
      };

      if (id) {
        await setDoc(doc(db, 'listings', id), listingData);
        try {
          await syncListingToBrowseCache(id, { ...listingData, id } as unknown as Listing);
        } catch (cacheErr) {
          console.warn('Browse cache sync failed', cacheErr);
        }
        await queryClient.invalidateQueries({ queryKey: PUBLISHED_LISTINGS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: ['listing'] });
        toast.success('Listing updated');
      } else {
        const newDocRef = doc(collection(db, 'listings'));
        await setDoc(newDocRef, { ...listingData, id: newDocRef.id });
        try {
          await syncListingToBrowseCache(newDocRef.id, {
            ...listingData,
            id: newDocRef.id,
          } as unknown as Listing);
        } catch (cacheErr) {
          console.warn('Browse cache sync failed', cacheErr);
        }
        await queryClient.invalidateQueries({ queryKey: PUBLISHED_LISTINGS_QUERY_KEY });
        toast.success('Listing created');
        navigate(`/admin/listings/${newDocRef.id}`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Error saving listing');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${index}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
        const storageRef = ref(storage, `listings/${fileName}`);
      await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const urls = await Promise.all(uploadPromises);
      setFormData({
        ...formData,
        images: [...(formData.images || []), ...urls],
      });
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (error: any) {
      toast.error('Error uploading images');
    }
  };

  const removeImage = (index: number) => {
    const newImages = [...(formData.images || [])];
    const removed = newImages.splice(index, 1)[0];
    setFormData({
      ...formData,
      images: newImages,
      coverImageUrl: formData.coverImageUrl === removed ? undefined : formData.coverImageUrl,
    });
  };

  const setCoverImage = (url: string) => {
    setFormData({ ...formData, coverImageUrl: url });
    toast.success('Cover image set');
  };

  const togglePresetAmenity = (name: string) => {
    const list = formData.amenities || [];
    if (list.includes(name)) {
      setFormData({ ...formData, amenities: list.filter((a) => a !== name) });
    } else {
      setFormData({ ...formData, amenities: [...list, name] });
    }
  };

  const addAmenity = () => {
    const amenity = prompt('Enter custom amenity:');
    if (amenity && !(formData.amenities || []).includes(amenity.trim())) {
      setFormData({
        ...formData,
        amenities: [...(formData.amenities || []), amenity.trim()],
      });
    }
  };

  const removeAmenity = (index: number) => {
    const newAmenities = [...(formData.amenities || [])];
    newAmenities.splice(index, 1);
    setFormData({ ...formData, amenities: newAmenities });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">{id ? 'Edit Listing' : 'New Listing'}</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Public URL slug (optional)
            </label>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                type="text"
                value={(formData.slug as string) || ''}
                onChange={(e) =>
                  setFormData({ ...formData, slug: slugifyForUrl(e.target.value) })
                }
                placeholder="e.g. bronze-bay-17"
                className="input flex-1"
              />
              <button
                type="button"
                className="btn btn-secondary text-sm shrink-0"
                onClick={() =>
                  setFormData({
                    ...formData,
                    slug: slugifyForUrl(formData.title || ''),
                  })
                }
              >
                Generate from title
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Guests can open this property at{' '}
              <code className="bg-gray-100 px-1 rounded">/your-slug</code> when published (example:{' '}
              <code className="bg-gray-100 px-1 rounded">/bronze-bay-17</code>). Use lowercase letters,
              numbers, and hyphens only.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={6}
              className="input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base Price (per night, Rands)
              </label>
              <input
                type="number"
                min={0}
                step="1"
                value={Math.round(((formData.basePrice as number) || 0) / 100)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    basePrice: Math.max(0, Math.round((parseFloat(e.target.value) || 0) * 100)),
                  })
                }
                required
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="input"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Featured on home page</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
              <input
                type="number"
                value={formData.bedrooms}
                onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 1 })}
                required
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
              <input
                type="number"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 1 })}
                required
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capacity</label>
              <input
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 2 })}
                required
                className="input"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">Start typing an address and select from suggestions</p>
              <GoogleMapsAutocomplete
                onPlaceSelect={(location) => {
                  setFormData({
                    ...formData,
                    location: {
                      ...location,
                      // Preserve existing values if new ones aren't provided
                      city: location.city || formData.location?.city || '',
                      country: location.country || formData.location?.country || 'South Africa',
                      region: location.region || formData.location?.region || '',
                    },
                  });
                  toast.success('Location set! Coordinates: ' + location.lat.toFixed(6) + ', ' + location.lng.toFixed(6));
                }}
                initialValue={formData.location?.address || ''}
                placeholder="Search for an address..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Address</label>
                <input
                  type="text"
                  placeholder="Address"
                  value={formData.location?.address || ''}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, address: e.target.value, lat: formData.location?.lat || 0, lng: formData.location?.lng || 0, city: formData.location?.city || '', country: formData.location?.country || 'South Africa' } })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">City</label>
                <input
                  type="text"
                  placeholder="City"
                  value={formData.location?.city || ''}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value, lat: formData.location?.lat || 0, lng: formData.location?.lng || 0, address: formData.location?.address || '', country: formData.location?.country || 'South Africa' } })}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Region/Province</label>
                <input
                  type="text"
                  placeholder="Region/Province"
                  value={formData.location?.region || ''}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, region: e.target.value, lat: formData.location?.lat || 0, lng: formData.location?.lng || 0, address: formData.location?.address || '', city: formData.location?.city || '', country: formData.location?.country || 'South Africa' } })}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Country</label>
                <input
                  type="text"
                  placeholder="Country"
                  value={formData.location?.country || 'South Africa'}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, country: e.target.value, lat: formData.location?.lat || 0, lng: formData.location?.lng || 0, address: formData.location?.address || '', city: formData.location?.city || '' } })}
                  className="input"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Latitude (auto-filled from address)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="Latitude"
                  value={formData.location?.lat || ''}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lat: parseFloat(e.target.value) || 0, lng: formData.location?.lng || 0, address: formData.location?.address || '', city: formData.location?.city || '', country: formData.location?.country || 'South Africa' } })}
                  className="input"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Longitude (auto-filled from address)</label>
                <input
                  type="number"
                  step="any"
                  placeholder="Longitude"
                  value={formData.location?.lng || ''}
                  onChange={(e) => setFormData({ ...formData, location: { ...formData.location, lng: parseFloat(e.target.value) || 0, lat: formData.location?.lat || 0, address: formData.location?.address || '', city: formData.location?.city || '', country: formData.location?.country || 'South Africa' } })}
                  className="input"
                  readOnly
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
            <p className="text-xs text-gray-500 mb-2">Click &quot;Set as cover&quot; to choose the main image for cards and search.</p>
            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="input mb-4" />
            {formData.images && formData.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative border rounded-lg overflow-hidden">
                    <img src={image} alt={`Image ${index + 1}`} className="w-full h-32 object-cover" />
                    {formData.coverImageUrl === image && (
                      <span className="absolute top-1 left-1 bg-[#32373c] text-white text-xs px-2 py-0.5 rounded">Cover</span>
                    )}
                    <div className="flex gap-1 p-1 bg-gray-50">
                      <button
                        type="button"
                        onClick={() => setCoverImage(image)}
                        className="flex-1 text-xs py-1 bg-white border rounded hover:bg-gray-100"
                      >
                        Set as cover
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="text-xs py-1 px-2 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amenities</label>
            <p className="text-xs text-gray-500 mb-2">Select from the list or add a custom amenity.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESET_AMENITIES.map((name) => {
                const selected = (formData.amenities || []).includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => togglePresetAmenity(name)}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      selected ? 'bg-[#32373c] text-white border-[#32373c]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={addAmenity} className="btn btn-secondary mb-4">
              + Add custom amenity
            </button>
            {formData.amenities && formData.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.amenities.map((amenity, index) => (
                  <span
                    key={`${amenity}-${index}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                  >
                    {amenity}
                    <button
                      type="button"
                      onClick={() => removeAmenity(index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pricing by date range
            </label>
            <PriceRulesEditor
              rules={formData.priceRules || []}
              basePriceCents={(formData.basePrice as number) || 0}
              onChange={(priceRules) => setFormData({ ...formData, priceRules })}
            />
          </div>

          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : 'Save Listing'}
          </button>
        </div>
      </form>
    </div>
  );
}
